import type { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core'
import { useWebSocket } from '@vueuse/core'
import type { AsyncData } from '#app/composables/asyncData'
import { useAsyncData } from '#app'
import type { CookieRef } from '#app/composables/cookie'
import {
    ClientError,
    type ExecuteOptions,
    NetworkClient,
    type NetworkRequest,
    type SubscriptionOptions,
} from '~/lib/bosca/networkclient'

export class NuxtNetworkClient extends NetworkClient {

    override get token(): string | null {
        const tokenCookie = useCookie('_bat')
        return tokenCookie.value || null
    }

    override set token(value: string | null | undefined) {
        let tries = 0
        while (tries < 10) {
            tries++
            // Get the config to match domain settings from backend
            const config = useRuntimeConfig()
            const cookieDomain = config.public.domain || undefined
            const tokenCookie = useCookie('_bat', {
                path: '/',           // Match the path setting
                domain: cookieDomain, // Match the domain setting
                httpOnly: false,     // Match the http_only setting
                sameSite: 'lax'      // Common security setting
            })
            if (value === null || value === undefined) {
                // Remove the cookie properly with matching properties
                tokenCookie.value = null

                // For some browsers, setting to null isn't enough
                // We need to set an expired date in the past
                document.cookie = '_bat=; path=/; domain=' +
                    (cookieDomain || '') +
                    '; expires=Thu, 01 Jan 1970 00:00:00 GMT';
            } else {
                // Set the cookie value
                tokenCookie.value = value
            }
            if (value && tokenCookie.value) {
                return
            }
            if (!value && !tokenCookie.value) {
                return
            }
        }
    }

    protected override async fetch(
        url: string,
        request: NetworkRequest,
    ): Promise<any> {
        return await $fetch(url, request as unknown as any)
    }

    override subscribe<T, V>(
        document: DocumentNode<T, V>,
        variables: any | null = null,
        options: SubscriptionOptions<T>,
    ) {
        if (import.meta.server) return
        const url = useRuntimeConfig().public.graphqlWsUrl
        let ids = new Date().getTime()
        // eslint-disable-next-line
        const self = this
        const { send } = useWebSocket(url, {
            autoReconnect: true,
            autoClose: true,
            autoConnect: true,
            onConnected: () => {
                send(JSON.stringify({
                    type: 'connection_init',
                    payload: { token: self.token },
                }))
            },
            onMessage: (socket, event) => {
                const body = JSON.parse(event.data)
                switch (body.type) {
                case 'connection_ack':
                    send(JSON.stringify({
                        id: (ids++).toString(),
                        type: 'subscribe',
                        payload: this.newQuery(document, variables),
                    }))
                    break
                case 'data':
                    if (body.errors) {
                        if (options.onError) {
                            options.onError(new ClientError(body.errors))
                        }
                    }
                    else {
                        options.onData(body.payload.data as T)
                    }
                    break
                case 'complete':
                    console.error('!!!!!complete!!!!!', body, event)
                    break
                default:
                    console.error('!!!!!unhandled!!!!', body, event)
                    break
                }
            },
            onError: (socket, event) => {
                console.error('!!!!!errror!!!!!!', event)
            },
            protocols: [
                'graphql-ws',
            ],
        })
    }

    executeAsyncData<T, V>(
        document: DocumentNode<T, V>,
        variables: V | any | null = null,
        options: ExecuteOptions | undefined = undefined,
        transform: (data: T | null) => T | null = data => data,
    ): AsyncData<T | null, any> {
        const refs = []
        if (variables) {
            for (const variable in variables) {
                const v = variables[variable]
                if (isRef(v)) {
                    refs.push(v)
                }
            }
        }
        const key = '/graphql' + JSON.stringify(this.newQuery(document, variables))
        // eslint-disable-next-line
        const self = this
        return useAsyncData(key, async (_) => {
            return await self.execute(document, variables, options)
        }, {
            watch: refs,
            deep: false,
            immediate: options?.immediate,
            transform,

        }) as AsyncData<T | null, any>
    }
}
